import type { StorefrontReviewSummary } from '@erp/shared';
import { Star } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '../../components/ui/index.js';
import { formatDate } from '../../lib/intl.js';
import { publicApi } from '../../lib/publicApi.js';

function Stars({ value, size = 'h-4 w-4', onPick }: { value: number; size?: string; onPick?: (n: number) => void }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= Math.round(value);
        const star = <Star className={`${size} ${filled ? 'fill-amber-400 text-amber-400' : 'text-fg-subtle'}`} aria-hidden />;
        return onPick ? (
          <button key={n} type="button" onClick={() => onPick(n)} aria-label={`${n}`} className="transition-transform hover:scale-110 active:scale-95">{star}</button>
        ) : <span key={n}>{star}</span>;
      })}
    </span>
  );
}

/** Storefront product reviews: average + approved list + a submit form (moderated). */
export function ProductReviews({ slug, itemId }: { slug: string; itemId: string }): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  const [data, setData] = useState<StorefrontReviewSummary | null>(null);
  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const load = (): void => {
    void publicApi().get<StorefrontReviewSummary>(`/${slug}/products/${itemId}/reviews`).then((r) => setData(r.data)).catch(() => setData({ average: 0, count: 0, items: [] }));
  };
  useEffect(load, [slug, itemId]);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!name.trim() || rating < 1) { toast.error(t('store.reviews.needRating', { defaultValue: 'Add your name and a star rating.' })); return; }
    setBusy(true);
    try {
      await publicApi().post(`/${slug}/products/${itemId}/reviews`, { name: name.trim(), rating, comment: comment.trim() || undefined });
      toast.success(t('store.reviews.submitted', { defaultValue: 'Thanks! Your review will appear once approved.' }));
      setName(''); setRating(0); setComment('');
    } catch { toast.error(t('store.reviews.failed', { defaultValue: 'Could not submit. Try again.' })); } finally { setBusy(false); }
  };

  return (
    <section className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <h2 className="text-xl font-semibold">{t('store.reviews.title', { defaultValue: 'Reviews' })}</h2>
        {data && data.count > 0 && (
          <span className="inline-flex items-center gap-2 text-sm text-fg-muted">
            <Stars value={data.average} />
            <span className="font-semibold text-fg">{data.average.toFixed(1)}</span>
            <span>({t('store.reviews.count', { defaultValue: '{{n}} reviews', n: data.count })})</span>
          </span>
        )}
      </div>

      {data && data.items.length > 0 && (
        <ul className="flex flex-col gap-3">
          {data.items.map((r) => (
            <li key={r.id} className="rounded-xl border border-border bg-bg-subtle p-4">
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{r.name}</span>
                <span className="text-xs text-fg-muted">{formatDate(r.createdAt, { locale: i18n.language })}</span>
              </div>
              <Stars value={r.rating} size="h-3.5 w-3.5" />
              {r.comment && <p className="mt-2 text-sm text-fg-muted">{r.comment}</p>}
            </li>
          ))}
        </ul>
      )}
      {data && data.count === 0 && <p className="text-sm text-fg-muted">{t('store.reviews.empty', { defaultValue: 'No reviews yet — be the first!' })}</p>}

      <form onSubmit={submit} className="flex flex-col gap-3 rounded-xl border border-border p-4">
        <h3 className="text-sm font-semibold">{t('store.reviews.write', { defaultValue: 'Write a review' })}</h3>
        <div className="flex items-center gap-3">
          <span className="text-sm text-fg-muted">{t('store.reviews.yourRating', { defaultValue: 'Your rating' })}</span>
          <Stars value={rating} size="h-6 w-6" onPick={setRating} />
        </div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('store.reviews.yourName', { defaultValue: 'Your name' })}
          maxLength={120}
          className="h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
        />
        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t('store.reviews.comment', { defaultValue: 'Your review (optional)' })}
          rows={3}
          maxLength={2000}
          className="w-full resize-y rounded-md border border-border bg-bg px-3 py-2 text-sm"
        />
        <div><Button type="submit" loading={busy}>{t('store.reviews.submit', { defaultValue: 'Submit review' })}</Button></div>
      </form>
    </section>
  );
}
